/**
 * D-EZ-109 — HEARD IN THE GARAGE: the week's quotes, each in the man's own voice.
 *
 * The people layer writes the words (`quote`, `factsForRace`, `victoryQuotes`);
 * this only decides WHO the Wire goes to this week — the winner, the man he
 * beat or his rival, the man who got wrecked and the man who did it, a driver
 * on a drought, the playoff's survivors and casualties — and frames each quote
 * with the plain fact it answers ("Wrecked by Ackerd; finished 31st").
 *
 * ⚠ NO WORDS IN A REAL MAN'S MOUTH. A 2025-roster driver (`rw25-…`) gets no
 * quote: the people layer returns none, and the Wire reports his result in the
 * third person instead.
 */

import { isRealDriver } from '../story/hometown.ts';
import { factsForRace, victoryQuotes, type DriverFact } from '../people/raceFacts.ts';
import { quote, slotsFor, type Quote, type QuoteFact } from '../people/quotes.ts';
import { hottestRivalries, pairKey } from '../people/rivalry.ts';
import { num, say, type WireLine } from './line.ts';
import type { WeekContext } from './week.ts';

export type QuoteRole = 'winner' | 'rival' | 'runner-up' | 'wrecked' | 'answer' | 'drought' | 'eliminated' | 'clinched' | 'back';

export interface WireQuote {
  role: QuoteRole;
  driverId: string;
  name: string;
  /** The plain fact he is answering, in the third person ("Wrecked by Ackerd; finished 31st"). */
  context: WireLine;
  /** His words, or null for a real 2025 driver (whose result is reported in `context` only). */
  quote: string | null;
  voice: string | null;
  /** The fact cell the quote came from, and the slot values it could use — for the grounding test. */
  factKind: string;
  quoteSlots: Record<string, string>;
  real: boolean;
}

const CONTEXT: Record<string, readonly string[]> = {
  won: ['Won the {race} at {track}.', 'Won at {track}.', 'The winner at {track}.', 'Took the {race}.'],
  'won-first': ['First career win, at {track}.', 'Won for the first time, at {track}.', 'A first career win, in the {race}.'],
  'won-deep': ['Won at {track} from {start} on the grid.', 'Came from {start} to win the {race}.', 'Started {start}, won at {track}.'],
  second: ['Second to {other} at {track}.', 'Ran second, behind {other}.', 'Runner-up to {other}.'],
  'good-run': ['Finished {finish} at {track}.', 'Brought it home {finish}.', 'A {finish}-place run at {track}.', 'Ran {finish}.'],
  'bad-day': ['Started {start}, finished {finish}.', 'Qualified {start}, faded to {finish}.', 'From {start} on the grid to {finish} at the flag.'],
  'wrecked-by': ['Wrecked by {other}; finished {finish}.', 'Put in the wall by {other} — {finish} at the flag.', 'Taken out by {other}, scored {finish}.'],
  wrecked: ['Took {other} out of the race.', 'The man who wrecked {other}.', 'Put {other} in the wall.'],
  'lost-lead-late': ['Lost the lead late to {other}.', 'Had it, then {other} took it away late.'],
  'dnf-mechanical': ['Out with a broken car, {finish}.', 'The car let him down; scored {finish}.', 'Parked by a mechanical failure, {finish}.'],
  clinched: ['Through to the next round.', 'Moves on in the playoff.'],
  'clinched-26': ['Locked into the playoff.', 'In the playoff field.'],
  eliminated: ['Knocked out of the playoff.', 'His title run is over.', 'Eliminated from the playoff.'],
  'rival-won': ['Watched his rival {other} win.', 'Saw {other} — of all people — win it.', 'Had to watch {other} celebrate.'],
  'injury-return': ['Back from injury; finished {finish}.', 'First race back from his injury: {finish}.'],
  milestone: ['His {n} start in this league.', 'Made start number {n} in this league.'],
};

/** The same facts, as a sentence about a real man (no words of his). */
const REPORT: Record<string, readonly string[]> = {
  won: ['{name} won the {race} at {track}.'],
  'won-first': ['{name} won for the first time in this league, at {track}.'],
  'won-deep': ['{name} won at {track} from {start} on the grid.'],
  second: ['{name} ran second to {other} at {track}.'],
  'good-run': ['{name} finished {finish} at {track}.'],
  'bad-day': ['{name} started {start} and finished {finish}.'],
  'wrecked-by': ['{name} was wrecked by {other} and finished {finish}.'],
  wrecked: ['{name} took {other} out of the race.'],
  'lost-lead-late': ['{name} lost the lead late to {other}.'],
  'dnf-mechanical': ['{name} went out with a broken car and was scored {finish}.'],
  clinched: ['{name} is through to the next round.'],
  'clinched-26': ['{name} is locked into the playoff.'],
  eliminated: ['{name} is out of the playoff.'],
  'rival-won': ['{name} watched his rival {other} win.'],
  'injury-return': ['{name} came back from injury and finished {finish}.'],
  milestone: ['{name} made his {n} start in this league.'],
};

function contextKey(df: DriverFact, w: WeekContext): string {
  const f = df.fact;
  if (f.kind === 'won') {
    if (f.first) return 'won-first';
    const start = w.run.startPositions[df.driverId];
    if (start !== undefined && start >= 10) return 'won-deep';
    return 'won';
  }
  if (f.kind === 'clinched' && w.raceNumber === 26) return 'clinched-26';
  return f.kind;
}

function contextLine(df: DriverFact, w: WeekContext, role: QuoteRole, real: boolean): WireLine | null {
  const f = df.fact;
  const me = w.run.race.finishingOrder.find((x) => x.driverId === df.driverId);
  const otherId = 'otherId' in f ? (f.otherId ?? null) : null;
  const slots = {
    name: w.nameOf(df.driverId),
    race: w.run.scheduled.raceName,
    track: w.trackShort,
    other: otherId ? w.short(otherId) : null,
    finish: me ? num(me.place, 'ord') : null,
    start: w.run.startPositions[df.driverId] !== undefined ? num(w.run.startPositions[df.driverId], 'ord') : null,
    n: f.kind === 'milestone' ? num(f.n, 'ord') : null,
  };
  const k = contextKey(df, w);
  const bank = real ? REPORT : CONTEXT;
  return say(`ctx-${role}-${k}`, bank[k] ?? [], slots, {
    key: `${w.seasonKey}|${df.driverId}|${role}`,
    occurrence: w.raceNumber,
    driverIds: [df.driverId, ...(otherId ? [otherId] : [])],
  });
}

function build(df: DriverFact, w: WeekContext, role: QuoteRole, given?: Quote | null): WireQuote | null {
  const real = isRealDriver(df.driverId);
  const context = contextLine(df, w, role, real);
  if (!context) return null;
  const q = real ? null : (given ?? quote(df.fact, df.driverId, w.people));
  if (!real && !q) return null;
  return {
    role,
    driverId: df.driverId,
    name: w.nameOf(df.driverId),
    context,
    quote: q?.text ?? null,
    voice: q?.voice ?? null,
    factKind: q?.kind ?? df.fact.kind,
    quoteSlots: slotsFor(df.fact as QuoteFact, w.people),
    real,
  };
}

/** The week's garage, at most `max` voices, no man twice. */
export function garageQuotes(w: WeekContext, max = 5): WireQuote[] {
  const extras = { sponsorOf: w.sponsorOf, teamOf: w.teamOf };
  const facts = factsForRace(w.people, w.seasonNumber, w.raceNumber, extras);
  const vq = victoryQuotes(w.people, w.seasonNumber, w.raceNumber, extras);
  const out: WireQuote[] = [];
  const used = new Set<string>();
  const add = (q: WireQuote | null): boolean => {
    if (!q || used.has(q.driverId) || out.length >= max) return false;
    out.push(q);
    used.add(q.driverId);
    return true;
  };
  const factOf = (id: string, kind: string, otherId?: string): DriverFact | undefined =>
    facts.find((f) => f.driverId === id && f.fact.kind === kind && (otherId === undefined || ('otherId' in f.fact && f.fact.otherId === otherId)));

  // 1. The winner.
  const winnerId = w.run.race.winnerId;
  const wf = winnerId ? factOf(winnerId, 'won') : undefined;
  if (wf) add(build(wf, w, 'winner', vq.winner));

  // 2. The man who answered him: his rival, else the runner-up.
  if (vq.other && winnerId) {
    const o = vq.other;
    const kind = o.quote.kind === 'second' ? 'second' : o.quote.kind === 'wrecked-by' ? 'wrecked-by' : o.quote.kind === 'lost-lead-late' ? 'lost-lead-late' : 'rival-won';
    const df = factOf(o.quote.driverId, kind) ?? ({ driverId: o.quote.driverId, fact: { ...(wf?.fact ?? {}), kind: 'rival-won', otherId: winnerId } } as DriverFact);
    add(build(df, w, o.rivalry ? 'rival' : 'runner-up', o.quote));
  } else {
    const second = facts.find((f) => f.fact.kind === 'second');
    if (second) add(build(second, w, 'runner-up'));
  }

  // 3 and 4. The man who got wrecked, and the man who did it. A wreck between
  // a pair the rivalry book already has hot comes first, then the victim
  // highest in the points.
  const hot = new Set(hottestRivalries(w.people.book, 12).map((r) => pairKey(r.a, r.b)));
  const wrecks = facts
    .filter((f) => f.fact.kind === 'wrecked-by' && !used.has(f.driverId))
    .sort((a, b) => {
      const ka = hot.has(pairKey(a.driverId, (a.fact as { otherId: string }).otherId)) ? 0 : 1;
      const kb = hot.has(pairKey(b.driverId, (b.fact as { otherId: string }).otherId)) ? 0 : 1;
      return ka - kb || (w.table.byDriver[a.driverId]?.rank ?? 99) - (w.table.byDriver[b.driverId]?.rank ?? 99);
    });
  for (const v of wrecks) {
    const aggressor = (v.fact as { otherId: string }).otherId;
    if (add(build(v, w, 'wrecked'))) {
      const reply = factOf(aggressor, 'wrecked', v.driverId);
      if (reply) add(build(reply, w, 'answer'));
      break;
    }
  }

  // The playoff: the round's casualties first, then the survivors.
  const eliminated = facts.filter((f) => f.fact.kind === 'eliminated' && !used.has(f.driverId));
  eliminated.sort((a, b) => (w.table.byDriver[a.driverId]?.rank ?? 99) - (w.table.byDriver[b.driverId]?.rank ?? 99));
  if (eliminated[0]) add(build(eliminated[0], w, 'eliminated'));

  // 5. A man on a drought: a past winner gone a long time without one, or a
  // top-fifteen man still looking for his first win of the year — who did
  // something this week worth asking him about.
  const droughts = facts
    .filter((f) => !used.has(f.driverId) && ['good-run', 'second', 'bad-day', 'wrecked-by', 'dnf-mechanical', 'lost-lead-late'].includes(f.fact.kind))
    .map((f) => {
      const s = w.people.streaks[f.driverId];
      const stat = w.table.byDriver[f.driverId];
      const long = s?.inDrought ? s.startsSinceWin : 0;
      const seasonless = stat && stat.wins === 0 && stat.rank <= 15 && w.raceNumber >= 6 ? stat.starts : 0;
      return { f, long, seasonless };
    })
    .filter((d) => d.long > 0 || d.seasonless > 0)
    .sort((a, b) => b.long - a.long || b.seasonless - a.seasonless);
  const d = droughts[0];
  if (d) {
    const q = build(d.f, w, 'drought');
    if (q) {
      const drought = d.long > 0
        ? say('ctx-drought-long', ['Winless in {n} starts. {fact}', '{n} starts since his last win. {fact}', 'Still waiting, {n} starts on. {fact}'], { n: num(d.long), fact: q.context.text }, { key: `${w.seasonKey}|${d.f.driverId}`, occurrence: w.raceNumber, driverIds: [d.f.driverId] })
        : say('ctx-drought-season', ['Still no win this year. {fact}', 'Winless this season, {rank} in points. {fact}', 'No trophy yet this year. {fact}'], { rank: num(w.table.byDriver[d.f.driverId].rank, 'ord'), fact: q.context.text }, { key: `${w.seasonKey}|${d.f.driverId}`, occurrence: w.raceNumber, driverIds: [d.f.driverId] });
      if (drought) {
        // The inner fact's own slots travel with it.
        drought.names = { ...q.context.names, ...drought.names };
        drought.nums = { ...q.context.nums, ...drought.nums };
        delete drought.names.fact;
        drought.driverIds = [...new Set([...drought.driverIds, ...q.context.driverIds])];
        q.context = drought;
      }
      add(q);
    }
  }

  // Anyone back from an injury, or clinched, if there is room.
  for (const kind of ['injury-return', 'clinched'] as const) {
    const f = facts.find((x) => x.fact.kind === kind && !used.has(x.driverId));
    if (f) add(build(f, w, kind === 'clinched' ? 'clinched' : 'back'));
  }
  return out;
}
